import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';
import { Location } from '@angular/common';
import { isRenderingForMobileApp } from 'src/constants';
import { Router } from '@angular/router';
import { TrxService } from '~services/trx/trx.service';
import { GetIdentityProviderResponse, IAuthProvider, IdentityProvider } from '~models';
import { CookieService } from 'ngx-cookie-service';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { FeatureFlagService } from '~services/feature-flag/feature-flag.service';
import { Auth0Service } from './auth0.service';
import { IDS4Service } from './ids4.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authProviderService: IAuthProvider;
  private useAuth0: boolean = false;

  constructor(
    private auth0Service: Auth0Service,
    private ids4Service: IDS4Service,
    private location: Location,
    private trxService: TrxService,
    private cookieService: CookieService,
    private router: Router,
    private ngHttp: HttpClient,
    private featureFlagService: FeatureFlagService,
  ) {}

  public async initialise() {
    this.useAuth0 =
      this.featureFlagService.isFeatureEnabled('loyalty-gift-cards-okta') ||
      isRenderingForMobileApp;
    await this.ids4Service.loadDiscoveryDocument();
    await this.handleProviderInit();
    await this.trxService.setCustomerId(this.customerId);
  }

  private async handleProviderInit() {
    const identityProvider = this.getInitialIdentityProvider();

    if (
      window.location.pathname.includes('/auth/') &&
      !window.location.pathname.includes('/auth/mobilesso')
    ) {
      this.setAuthProviderService(identityProvider);
      return;
    }

    if (await this.isAuthenticatedWithAny()) return;

    if (await this.silentRefresh(identityProvider)) return;

    // Go to IDS4 login page for mobile app if silent refresh failed
    if (isRenderingForMobileApp) {
      this.setAuthProviderService(IdentityProvider.IdentityServer4);
      this.navigateToLoginPage(this.location.path());
      return;
    }

    if (!this.useAuth0) {
      this.setAuthProviderService(IdentityProvider.IdentityServer4);
      return;
    }

    const params: Record<string, string> = {
      customerId: this.customerId as string,
    };

    try {
      const response = await this.ngHttp
        .get<GetIdentityProviderResponse>(`${environment.apiHost}/auth/identity-provider`, {
          params,
        })
        .toPromise();
      this.setAuthProviderService(response.data);
    } catch (error) {
      this.setAuthProviderService(IdentityProvider.IdentityServer4);
    }
  }

  private async isAuthenticatedWithAny() {
    const isLoggedInIDS4 = await this.ids4Service.isAuthenticated();
    if (isLoggedInIDS4) {
      this.setAuthProviderService(IdentityProvider.IdentityServer4);
      return true;
    }

    const isLoggedInOkta = this.useAuth0 && (await this.auth0Service.isAuthenticated());
    if (isLoggedInOkta) {
      this.setAuthProviderService(IdentityProvider.Okta);
      return true;
    }

    return false;
  }

  private async silentRefresh(initialIdentityProvider: IdentityProvider) {
    const auth0SilentRefresh = async () => {
      if (this.useAuth0 && (await this.auth0Service.silentRefresh())) {
        this.authProviderService = this.auth0Service;
        await this.afterAuth();
        return true;
      }
      return false;
    };

    const ids4SilentRefresh = async () => {
      if (await this.ids4Service.silentRefresh()) {
        this.authProviderService = this.ids4Service;
        await this.afterAuth();
        return true;
      }
      return false;
    };

    if (initialIdentityProvider === IdentityProvider.Okta) {
      if (await auth0SilentRefresh()) return true;
      return await ids4SilentRefresh();
    }

    if (await ids4SilentRefresh()) return true;
    return await auth0SilentRefresh();
  }

  private setAuthProviderService(identityProvider: IdentityProvider) {
    this.authProviderService =
      identityProvider === IdentityProvider.Okta ? this.auth0Service : this.ids4Service;
    sessionStorage.setItem(
      'zip-gift-cards-identity-provider',
      this.authProviderService.identityProvider,
    );
  }

  private async afterAuth() {
    await this.authProviderService.afterAuth();

    const cookieOptions = {
      domain: `.${window.location.hostname}`,
      path: '/',
      secure: true,
      expires: moment().add({ years: 1 }).toDate(),
    };

    this.cookieService.set('zip_id', this.authProviderService.customerId, cookieOptions);

    sessionStorage.setItem(
      'zip-gift-cards-identity-provider',
      this.authProviderService.identityProvider,
    );

    this.cookieService.set('idp_cookie', this.authProviderService.identityProvider, cookieOptions);
  }

  private getInitialIdentityProvider() {
    const provider =
      sessionStorage.getItem('zip-gift-cards-identity-provider') ??
      this.cookieService.get('idp_cookie');

    if (provider !== IdentityProvider.Okta && provider !== IdentityProvider.IdentityServer4) {
      return IdentityProvider.IdentityServer4;
    }

    return provider;
  }

  public navigateToLoginPage(returnTo: string) {
    this.router.navigateByUrl(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  }

  public async isAuthenticated() {
    return !!(await this.authProviderService?.isAuthenticated());
  }

  public async handleLogin(returnTo: string) {
    await this.authProviderService.redirectToLogin(returnTo);
  }

  public async handleCallback() {
    const returnTo = await this.authProviderService.handleCallback();
    await this.afterAuth();

    return returnTo;
  }

  public async getAccessToken() {
    return await this.authProviderService?.getAccessToken();
  }

  public get identityProvider() {
    return this.authProviderService.identityProvider;
  }

  public get customerId() {
    return this.authProviderService?.customerId ?? this.cookieService.get('zip_id');
  }
}
